import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import {
  IosShareOutlined,
  AddToHomeScreenOutlined,
  InstallMobileOutlined,
  NotificationsActiveOutlined,
  CheckCircleOutline,
  ContentCopyOutlined,
  MoreVertOutlined,
  MoreHorizOutlined,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
} from "@mui/icons-material";
import {
  isIosSafari,
  isIphone,
  getIosBrowser,
  getIosVersion,
  isStandaloneDisplay,
} from "../pwa/pushClient";
import { openAppStore } from "../native/appStore";

const ACCENT = "#7fb09a";

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

function Step({ icon, children }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          flexShrink: 0,
          width: 34,
          height: 34,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: `${ACCENT}22`,
          color: ACCENT,
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2">{children}</Typography>
    </Stack>
  );
}

/**
 * A fixed, bouncing arrow that points at the browser control the user must tap
 * to install on iOS (Apple blocks any programmatic "add to home screen"):
 *   - Safari ≤18: the Share button lives bottom-centre → arrow points down there.
 *   - Safari (iOS 26): Share hides in a "•••" menu bottom-right → arrow down-right.
 *   - Chrome/Edge: the menu lives top-right → arrow points up there.
 * Placement is "bottom-center" | "bottom-right" | "top-right". Purely a visual
 * nudge: fixed-position, non-interactive, never blocks taps.
 */
function InstallPointer({ placement, label, icon }) {
  const bottom = placement.startsWith("bottom");
  const alignRight = placement.endsWith("right");
  const bounce = bottom ? "phiegPointDown" : "phiegPointUp";
  const Chevron = bottom ? KeyboardArrowDownRounded : KeyboardArrowUpRounded;

  const Pill = (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        backgroundColor: ACCENT,
        color: "#fff",
        boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
        maxWidth: "82vw",
      }}
    >
      {icon}
      <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
        {label}
      </Typography>
    </Stack>
  );

  const Arrow = (
    <Chevron
      sx={{
        fontSize: 44,
        color: ACCENT,
        filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.35))",
        animation: `${bounce} 1.1s ease-in-out infinite`,
        "@keyframes phiegPointDown": {
          "0%,100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(9px)" },
        },
        "@keyframes phiegPointUp": {
          "0%,100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-9px)" },
        },
      }}
    />
  );

  return (
    <Box
      sx={{
        position: "fixed",
        zIndex: 2000,
        pointerEvents: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: alignRight ? "flex-end" : bottom ? "center" : "flex-end",
        ...(bottom
          ? {
              bottom: "calc(env(safe-area-inset-bottom, 0px) + 4px)",
              ...(alignRight ? { right: 12 } : { left: 0, right: 0 }),
            }
          : { right: 12, top: "calc(env(safe-area-inset-top, 0px) + 6px)" }),
      }}
    >
      {bottom ? (
        <>
          {Pill}
          {Arrow}
        </>
      ) : (
        <>
          {Arrow}
          {Pill}
        </>
      )}
    </Box>
  );
}

/**
 * Public, store-free install page. Send a client the link (or a QR) and this
 * page walks them through adding Phieg CRM to their home screen:
 *   - Android: one-tap install via the captured beforeinstallprompt event.
 *   - iOS: Share -> Add to Home Screen instructions (Apple blocks programmatic
 *     install).
 *   - Desktop: a QR code to continue on a phone.
 */
export default function GetApp() {
  const theme = useTheme();
  const surface = theme.palette.extras?.surface || {};

  const [deferredPrompt, setDeferredPrompt] = useState(
    typeof window !== "undefined" ? window.__deferredInstallPrompt || null : null
  );
  const [installed, setInstalled] = useState(isStandaloneDisplay());
  const [copied, setCopied] = useState(false);

  const appUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.phiegcrm.com";

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      window.__deferredInstallPrompt = e;
      setDeferredPrompt(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const evt = deferredPrompt || window.__deferredInstallPrompt;
    if (!evt) return;
    evt.prompt();
    try {
      await evt.userChoice;
    } catch (_) {
      /* user dismissed */
    }
    window.__deferredInstallPrompt = null;
    setDeferredPrompt(null);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(appUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      /* clipboard unavailable */
    }
  };

  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  // On iOS the native share sheet does NOT contain "Add to Home Screen", so
  // showing a Share button there only confuses — restrict it to non-iOS.
  const showShare = canShare && !isIosSafari();
  const handleShare = async () => {
    try {
      await navigator.share({
        title: "Phieg CRM",
        text: "Install the Phieg CRM app — your leads, straight to your phone.",
        url: appUrl,
      });
    } catch (_) {
      /* user cancelled or unsupported */
    }
  };

  // An iPhone gets the App Store app (iPhone-only listing); an iPad keeps the
  // Home Screen web app and the install guide below.
  const iphone = isIphone();
  const ios = isIosSafari() && !iphone;
  const iosBrowser = getIosBrowser(); // "safari" | "chrome" | "edge" | "firefox" | null
  const iosVersion = getIosVersion(); // major version, or null when unknown
  const android = isAndroid();
  const desktop = !ios && !iphone && !android;
  const canInstallNow = Boolean(deferredPrompt);

  // Where to point the install arrow:
  //   - Chrome/Edge expose Share from a "⋮" menu at the TOP-RIGHT.
  //   - iOS 26's redesigned Safari (the default "Compact" layout) hides Share
  //     behind a "•••" menu at the BOTTOM-RIGHT — no more bottom-centre button.
  //   - Older Safari keeps the classic bottom-centre Share button.
  // We can only read the OS version, not the chosen toolbar layout, so iOS 26+
  // Safari users are steered to the new default location. Firefox can't install.
  const topRightBrowser = iosBrowser === "chrome" || iosBrowser === "edge";
  const newSafari =
    iosBrowser === "safari" && typeof iosVersion === "number" && iosVersion >= 26;
  const pointerPlacement = topRightBrowser
    ? "top-right"
    : newSafari
    ? "bottom-right"
    : "bottom-center";
  const showIosPointer = ios && !installed && iosBrowser !== "firefox";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 440,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          backgroundColor: surface.raised || theme.palette.background.paper,
          border: `1px solid ${surface.borderSubtle || theme.palette.divider}`,
          boxShadow: theme.palette.extras?.shadow?.md,
        }}
      >
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <Box
            component="img"
            src="/logo192.png"
            alt="Phieg CRM"
            sx={{
              width: 72,
              height: 72,
              borderRadius: 2.5,
              boxShadow: theme.palette.extras?.shadow?.sm,
            }}
          />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Get the Phieg CRM app
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: surface.textMid || theme.palette.text.secondary }}
            >
              {iphone
                ? "Install it from the App Store — every new lead, straight to your phone with instant notifications."
                : "Add it to your home screen — every new lead, straight to your phone with instant notifications. No app store needed."}
            </Typography>
          </Box>

          {iphone ? (
            <Box sx={{ width: "100%" }}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                startIcon={<InstallMobileOutlined />}
                onClick={() => openAppStore()}
                sx={{
                  backgroundColor: ACCENT,
                  "&:hover": { backgroundColor: "#5f9078" },
                  py: 1.25,
                }}
              >
                Get it on the App Store
              </Button>
              <Typography variant="body2" sx={{ mt: 1.5, color: surface.textMid }}>
                Install, open Phieg, sign in with your usual details and turn
                notifications on when asked.
              </Typography>
            </Box>
          ) : installed ? (
            <Stack spacing={1} alignItems="center" sx={{ py: 2 }}>
              <CheckCircleOutline sx={{ fontSize: 48, color: ACCENT }} />
              <Typography variant="subtitle1" fontWeight={600}>
                You're all set
              </Typography>
              <Typography variant="body2" sx={{ color: surface.textMid }}>
                Phieg CRM is already installed on this device.
              </Typography>
            </Stack>
          ) : (
            <Box sx={{ width: "100%" }}>
              {/* Android — one-tap install when the browser offers it */}
              {android && canInstallNow ? (
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  startIcon={<InstallMobileOutlined />}
                  onClick={handleInstall}
                  sx={{
                    backgroundColor: ACCENT,
                    "&:hover": { backgroundColor: "#5f9078" },
                    py: 1.25,
                  }}
                >
                  Install app
                </Button>
              ) : null}

              {/* Android fallback — manual instructions */}
              {android && !canInstallNow ? (
                <Stack spacing={1.75} sx={{ textAlign: "left" }}>
                  <Step icon={<InstallMobileOutlined fontSize="small" />}>
                    Open this page in <strong>Chrome</strong>.
                  </Step>
                  <Step icon={<AddToHomeScreenOutlined fontSize="small" />}>
                    Tap the <strong>⋮</strong> menu, then{" "}
                    <strong>Install app</strong> (or “Add to Home screen”).
                  </Step>
                </Stack>
              ) : null}

              {/* iOS — Apple requires a manual Add to Home Screen. We can't
                  trigger it, so we point the user straight at the right
                  control for their browser. */}
              {ios && iosBrowser === "firefox" ? (
                <Stack spacing={1.75} sx={{ textAlign: "left" }}>
                  <Step icon={<IosShareOutlined fontSize="small" />}>
                    Firefox can’t add apps to the home screen. Open this page in{" "}
                    <strong>Safari</strong> or <strong>Chrome</strong> first.
                  </Step>
                  <Step icon={<AddToHomeScreenOutlined fontSize="small" />}>
                    Then tap <strong>Share</strong> →{" "}
                    <strong>Add to Home Screen</strong>.
                  </Step>
                </Stack>
              ) : null}

              {ios && topRightBrowser ? (
                <Stack spacing={1.75} sx={{ textAlign: "left" }}>
                  <Step icon={<MoreVertOutlined fontSize="small" />}>
                    Tap the <strong>⋯ menu</strong> at the{" "}
                    <strong>top-right</strong> — the arrow is pointing at it.
                  </Step>
                  <Step icon={<IosShareOutlined fontSize="small" />}>
                    Tap <strong>Share…</strong> in the menu.
                  </Step>
                  <Step icon={<AddToHomeScreenOutlined fontSize="small" />}>
                    Tap <strong>Add to Home Screen</strong>.
                  </Step>
                </Stack>
              ) : null}

              {/* iOS 26 Safari (default "Compact" layout) — Share now lives in
                  a "•••" menu at the bottom-right, not the old bottom button. */}
              {newSafari ? (
                <Stack spacing={1.75} sx={{ textAlign: "left" }}>
                  <Step icon={<MoreHorizOutlined fontSize="small" />}>
                    Tap the <strong>•••</strong> menu at the{" "}
                    <strong>bottom-right</strong> — the arrow is pointing at it.
                  </Step>
                  <Step icon={<IosShareOutlined fontSize="small" />}>
                    Tap <strong>Share</strong> in the menu (some versions list{" "}
                    <strong>Add to Home Screen</strong> there directly).
                  </Step>
                  <Step icon={<AddToHomeScreenOutlined fontSize="small" />}>
                    Tap <strong>Add to Home Screen</strong>, then open Phieg CRM
                    from your home screen.
                  </Step>
                </Stack>
              ) : null}

              {ios && !topRightBrowser && !newSafari && iosBrowser !== "firefox" ? (
                <Stack spacing={1.75} sx={{ textAlign: "left" }}>
                  <Step icon={<IosShareOutlined fontSize="small" />}>
                    Tap the <strong>Share</strong> button at the{" "}
                    <strong>bottom</strong> — the arrow is pointing at it.
                  </Step>
                  <Step icon={<AddToHomeScreenOutlined fontSize="small" />}>
                    Scroll down and tap{" "}
                    <strong>Add to Home Screen</strong>.
                  </Step>
                  <Step icon={<CheckCircleOutline fontSize="small" />}>
                    Open Phieg CRM from your home screen.
                  </Step>
                </Stack>
              ) : null}

              {/* Desktop — scan to continue on a phone */}
              {desktop ? (
                <Stack spacing={2} alignItems="center">
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: "#fff",
                      lineHeight: 0,
                    }}
                  >
                    <QRCodeSVG value={appUrl} size={184} level="M" />
                  </Box>
                  <Typography variant="body2" sx={{ color: surface.textMid }}>
                    Scan with your phone's camera to open this page there, then
                    install.
                  </Typography>
                </Stack>
              ) : null}

              {/* Native share opens the "send a link" sheet, NOT the Safari
                  toolbar sheet that holds "Add to Home Screen" — showing it on
                  iOS sends people to the wrong place, so it's hidden there. */}
              {showShare ? (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<IosShareOutlined />}
                  onClick={handleShare}
                  sx={{
                    mt: 2.5,
                    bgcolor: ACCENT,
                    "&:hover": { bgcolor: "#5f9078" },
                  }}
                >
                  Share app link
                </Button>
              ) : null}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ContentCopyOutlined />}
                onClick={handleCopy}
                sx={{ mt: showShare ? 1 : 2.5 }}
              >
                {copied ? "Link copied!" : "Copy link"}
              </Button>
            </Box>
          )}

          <Stack
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
            sx={{
              mt: 1,
              pt: 2,
              borderTop: `1px solid ${surface.borderSubtle || theme.palette.divider}`,
              width: "100%",
            }}
          >
            <NotificationsActiveOutlined sx={{ color: ACCENT, fontSize: 20, mt: "2px" }} />
            <Typography variant="caption" sx={{ color: surface.textMid, textAlign: "left" }}>
              After installing, open the app and turn on notifications in
              Settings to be alerted the moment a lead is assigned to you.
            </Typography>
          </Stack>
        </Stack>
      </Card>

      {showIosPointer ? (
        <InstallPointer
          placement={pointerPlacement}
          icon={
            topRightBrowser ? (
              <MoreVertOutlined sx={{ fontSize: 18 }} />
            ) : newSafari ? (
              <MoreHorizOutlined sx={{ fontSize: 18 }} />
            ) : (
              <IosShareOutlined sx={{ fontSize: 18 }} />
            )
          }
          label={
            topRightBrowser
              ? "Tap the ⋯ menu up here → Add to Home Screen"
              : newSafari
              ? "Tap ••• here → Share → Add to Home Screen"
              : "Tap Share here → Add to Home Screen"
          }
        />
      ) : null}
    </Box>
  );
}
